import type { MaybeRefOrGetter } from 'vue'
import type { LandingTemplateButtonsMap } from '~/entities/projects/model/landingTemplateButtons'
import type { TemplateData } from '~/entities/projects/model/templates-interface'
import { computed, toValue } from 'vue'
import { serializeLandingTemplateButtonsForSave } from '~/entities/projects/model/landingTemplateButtons'
import {
  useCreateOneLandingTemplateMutation,
  useGetLandingTemplatesQuery,
  useUpdateOneLandingTemplateMutation,
} from '~/shared/api/client'
import { isUuid } from '~/shared/lib'

export interface TemplateManagerScope {
  projectId?: string
  projectRequestId?: string
}

interface TemplateManagerOptions {
  enabled?: MaybeRefOrGetter<boolean>
}

export function useTemplateManager(
  getScope: () => TemplateManagerScope,
  options?: TemplateManagerOptions,
) {
  const { mutate: createTemplate } = useCreateOneLandingTemplateMutation()
  const { mutate: updateTemplate } = useUpdateOneLandingTemplateMutation()

  const { result: templateData } = useGetLandingTemplatesQuery(
    () => {
      const s = getScope()
      const filter = s.projectRequestId
        ? { projectRequestId: { eq: s.projectRequestId } }
        : { projectId: { eq: s.projectId! } }
      return {
        paging: { first: 1 },
        filter,
        sorting: [],
      }
    },
    {
      fetchPolicy: 'cache-and-network',
      enabled: computed(() => {
        if (options?.enabled !== undefined && !toValue(options.enabled)) {
          return false
        }

        const s = getScope()
        if (s.projectRequestId) {
          return true
        }
        return Boolean(s.projectId && isUuid(s.projectId))
      }),
    },
  )

  const hasExistingTemplate = computed(() => {
    return Boolean(templateData.value?.landingTemplates?.edges?.length)
  })

  const existingTemplate = computed(() => {
    return hasExistingTemplate.value ? templateData.value?.landingTemplates.edges[0].node : null
  })

  function prepareTemplateContentForSave(templateContent: TemplateData) {
    const existingContent = (existingTemplate.value?.content ?? {}) as Record<string, unknown>
    const nextContent = {
      ...existingContent,
      ...templateContent,
    } as Record<string, unknown>

    if ('buttons' in templateContent) {
      nextContent.buttons = serializeLandingTemplateButtonsForSave(
        templateContent.buttons as LandingTemplateButtonsMap | undefined,
      )
    }

    return nextContent
  }

  async function patchLandingTemplateButtons(buttons: LandingTemplateButtonsMap) {
    if (!hasExistingTemplate.value || !existingTemplate.value?.id) {
      return false
    }

    const existingContent = (existingTemplate.value.content ?? {}) as Record<string, unknown>

    await updateTemplate({
      input: {
        id: existingTemplate.value.id,
        update: {
          content: {
            ...existingContent,
            buttons: serializeLandingTemplateButtonsForSave(buttons),
          },
          isPublic: true,
        },
      },
    })

    return true
  }

  async function saveTemplate(templateContent: TemplateData) {
    const contentForSave = prepareTemplateContentForSave(templateContent)

    if (hasExistingTemplate.value && existingTemplate.value?.id) {
      return await updateTemplate({
        input: {
          id: existingTemplate.value.id,
          update: {
            content: contentForSave as any,
            isPublic: true,
          },
        },
      })
    }
    else {
      const s = getScope()
      const landingBody = {
        name: (templateContent as any).title || (templateContent as any).name || 'Без названия',
        description: templateContent?.description,
        content: contentForSave as any,
        isPublic: true,
        ...(s.projectRequestId
          ? { projectRequestId: s.projectRequestId }
          : { projectId: s.projectId! }),
      }
      return await createTemplate({
        input: {
          landingTemplate: landingBody,
        },
      })
    }
  }

  return {
    templateData,
    hasExistingTemplate,
    existingTemplate,
    saveTemplate,
    patchLandingTemplateButtons,
  }
}
